import React, { useEffect, useMemo, useReducer, useState } from 'react';
import { Alert, Button, Spinner, Table } from 'react-bootstrap';
import { Autocomplete, Box, TextField, Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import {
    CashOrderReport,
    CashOrderReportFilters,
    CashOrderReportRow,
    CashOrderReportType,
} from '../../models/cash-order-report';
import {
    getAllCashOrderReportRows,
    getCashOrderReport,
    getDepositBranches,
    getDepositClients,
} from '../../utils/deposit-turnover-hooks';
import { getCashOrderPdf } from '../../utils/clients-hooks';
import { DatePickerWrapper } from '../../components/DatePickerWrapper';
import {createDepositReportState, depositReportReducer} from './depositReportReducer';
import { PageSizeComponent } from '../../components/PageSizeComponent';
import { PaginationComponent } from '../../components/Pagination';
import { usePagination } from '../../utils/pagination-hooks';

const today = () => new Intl.DateTimeFormat('sv-SE', { timeZone: 'Europe/Sofia' }).format(new Date());
const money = (minor: number, currency: string) =>
    `${(minor / 100).toLocaleString('bg-BG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency}`;
const date = (value: string) =>
    new Intl.DateTimeFormat('bg-BG', { timeZone: 'Europe/Sofia', day: '2-digit', month: '2-digit', year: 'numeric' })
        .format(new Date(value))
        .replace(/ г\.$/, '');
const client = (row: CashOrderReportRow) => row.clientName + (row.eik ? ` / ${row.eik}` : '');
const types: CashOrderReportType[] = ['INCOMING', 'OUTGOING'];

const OrderTable = ({
    filters,
    type,
    onPrint,
    pageSize,
}: {
    filters: CashOrderReportFilters;
    type: CashOrderReportType;
    onPrint: (id: number) => void;
    pageSize: number;
}) => {
    const { t } = useTranslation('translation', { keyPrefix: 'depositReport' });
    const pagination = usePagination(pageSize);
    const page = pagination.currentPage - 1;

    const [data, setData] = useState<CashOrderReport>();
    const [error, setError] = useState(false);
    const [retry, setRetry] = useState(0);

    useEffect(() => {
        let active = true;
        setData(undefined);
        setError(false);
        getCashOrderReport(filters, type, page, pageSize)
            .then((result) => {
                if (active) setData(result);
            })
            .catch(() => {
                if (active) setError(true);
            });
        return () => {
            active = false;
        };
    }, [filters, type, page, pageSize, retry]);

    return (
        <section className='mt-4'>
            <h5>{t(type)}</h5>
            {error ? (
                <Alert variant='danger'>
                    {t('error')} <Button onClick={() => setRetry((value) => value + 1)}>{t('retry')}</Button>
                </Alert>
            ) : !data ? (
                <div className='d-flex justify-content-center w-100 py-3' role='status' aria-busy='true'>
                    <Spinner animation='border' />
                </div>
            ) : (
                <>
                    <Table striped bordered hover responsive className='deposit-report-table'>
                        <thead>
                            <tr>
                                {['number', 'date', 'clientEik', 'branch', 'distributor', 'amount'].map((key) => (
                                    <th key={key} className={key === 'amount' ? 'text-end' : undefined}>
                                        {t(key)}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {data.items.map((row) => (
                                <tr
                                    key={row.id}
                                    tabIndex={0}
                                    aria-label={`${t('openPdf')} ${row.documentNumber}`}
                                    className={row.status === 'VOID' ? 'table-secondary' : undefined}
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => onPrint(row.id)}
                                    onKeyDown={(event) => {
                                        if (
                                            event.target === event.currentTarget &&
                                            (event.key === 'Enter' || event.key === ' ')
                                        ) {
                                            event.preventDefault();
                                            onPrint(row.id);
                                        }
                                    }}
                                >
                                    <td>
                                        {row.documentNumber}
                                        {row.status === 'VOID' && <strong className='ms-2'>{t('VOID')}</strong>}
                                    </td>
                                    <td className='text-nowrap'>{date(row.issuedAt)}</td>
                                    <td className='text-nowrap'>{client(row)}</td>
                                    <td>{row.branchName}</td>
                                    <td>{row.distributorName}</td>
                                    <td className='text-end text-nowrap'>
                                        <Tooltip
                                            arrow
                                            placement='top-end'
                                            componentsProps={{
                                                tooltip: {
                                                    sx: {
                                                        bgcolor: 'background.paper',
                                                        color: 'text.primary',
                                                        border: 1,
                                                        borderColor: 'divider',
                                                        borderRadius: 2,
                                                        boxShadow: 4,
                                                        p: 1.5,
                                                        maxWidth: 'min(440px, calc(100vw - 24px))',
                                                    },
                                                },
                                                arrow: {sx: {color: 'background.paper'}},
                                            }}
                                            title={
                                                <Box sx={{maxHeight: 320, overflow: 'auto'}}>
                                                <Box component='table' sx={{
                                                    width: '100%',
                                                    borderCollapse: 'collapse',
                                                    fontSize: 13,
                                                    lineHeight: 1.5,
                                                    '& th, & td': {py: 0.75, px: 1, textAlign: 'left'},
                                                    '& th:first-of-type, & td:first-of-type': {pl: 0},
                                                    '& th:last-child, & td:last-child': {pr: 0, textAlign: 'right', whiteSpace: 'nowrap'},
                                                    '& thead th': {color: 'text.secondary', fontWeight: 600, borderBottom: 1, borderColor: 'divider', whiteSpace: 'nowrap'},
                                                    '& tbody td': {fontVariantNumeric: 'tabular-nums'},
                                                    '& tfoot th': {pt: 1, fontWeight: 700, borderTop: 1, borderColor: 'divider'},
                                                }}>
                                                    <thead>
                                                        <tr>
                                                            <th>{t('device')}</th>
                                                            <th>{t('model')}</th>
                                                            <th>{t('amount')}</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {row.items.map((item, index) => (
                                                            <tr key={`${item.materialId != null ? 'material' : 'device'}-${item.materialId ?? item.deviceId}-${index}`}>
                                                                <td>{item.materialId ?? item.deviceId}</td>
                                                                <td>{item.deviceModel}</td>
                                                                <td className='text-end'>
                                                                    {money(item.amountMinor, row.currency)}
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                    <tfoot>
                                                        <tr>
                                                            <th colSpan={2}>{t('totals')}</th>
                                                            <th>{money(row.totalAmountMinor, row.currency)}</th>
                                                        </tr>
                                                    </tfoot>
                                                </Box>
                                                </Box>
                                            }
                                        >
                                            <button
                                                className='btn btn-link p-0 text-end'
                                                aria-label={`${t('openPdf')} ${row.documentNumber}: ${money(
                                                    row.totalAmountMinor,
                                                    row.currency
                                                )}`}
                                            >
                                                {money(row.totalAmountMinor, row.currency)}
                                            </button>
                                        </Tooltip>
                                    </td>
                                </tr>
                            ))}
                            {!data.items.length && (
                                <tr>
                                    <td colSpan={6}>{t('empty')}</td>
                                </tr>
                            )}
                        </tbody>
                    </Table>
                    <div className='d-flex justify-content-between align-items-center flex-wrap gap-2'>
                        <div>
                            {t('issuedTotal')}:{' '}
                            {data.totals.map((total) => money(total.amountMinor, total.currency)).join(' · ') || '—'}
                        </div>
                        <PaginationComponent recordsCount={data.totalElements} pagination={pagination} />
                    </div>
                </>
            )}
        </section>
    );
};

export const DepositTurnoverReport = ({ users }: { users: { id: number; name: string }[] }) => {
    const pageSizeSelection = usePagination(50);
    const { t } = useTranslation('translation', { keyPrefix: 'depositReport' });
    const [state, dispatch] = useReducer(depositReportReducer, undefined, () => createDepositReportState(today()));
    const {selection, clients, branches, directoryRetry, actionError, exporting} = state;
    const {afterDate, beforeDate, distributorId, selectedClient, selectedBranch, direction} = selection;
    const validDates = !!afterDate && !!beforeDate && afterDate <= beforeDate;
    const filters = useMemo(
        () => ({
            afterDate,
            beforeDate,
            distributorId,
            clientId: selectedClient?.id,
            branchId: selectedBranch?.id,
            direction,
        }),
        [afterDate, beforeDate, distributorId, selectedClient, selectedBranch, direction]
    );

    useEffect(() => {
        let active = true;
        dispatch({type: 'directoryStarted', directory: 'clients'});
        getDepositClients(distributorId)
            .then((result) => {
                if (active) dispatch({type: 'directoryLoaded', directory: 'clients', items: result});
            })
            .catch(() => {
                if (active) dispatch({type: 'directoryFailed', directory: 'clients'});
            });
        return () => {
            active = false;
        };
    }, [distributorId, directoryRetry]);

    useEffect(() => {
        let active = true;
        if (!selectedClient) return;
        dispatch({type: 'directoryStarted', directory: 'branches'});
        getDepositBranches(selectedClient.id, distributorId)
            .then((result) => {
                if (active) dispatch({type: 'directoryLoaded', directory: 'branches', items: result});
            })
            .catch(() => {
                if (active) dispatch({type: 'directoryFailed', directory: 'branches'});
            });
        return () => {
            active = false;
        };
    }, [selectedClient, distributorId, directoryRetry]);

    const print = async (id: number) => {
        const tab = window.open('', '_blank');
        if (!tab) {
            dispatch({type: 'actionFailed'});
            return;
        }
        tab.opener = null;
        try {
            const pdf = await getCashOrderPdf(id);
            const url = URL.createObjectURL(pdf);
            tab.location.href = url;
            window.setTimeout(() => URL.revokeObjectURL(url), 60000);
        } catch {
            tab.close();
            dispatch({type: 'actionFailed'});
        }
    };

    const exportReport = async () => {
        dispatch({type: 'exportStarted'});
        try {
            const workbook = new ExcelJS.Workbook();
            for (const type of types) {
                const { rows, totals } = await getAllCashOrderReportRows(filters, type);
                const sheet = workbook.addWorksheet(t(type));
                sheet.addRow([
                    afterDate,
                    beforeDate,
                    users.find((user) => user.id === distributorId)?.name ?? t('all'),
                    selectedClient?.name ?? t('all'),
                    selectedBranch?.name ?? t('all'),
                ]);
                sheet.addRow(
                    ['number', 'date', 'clientEik', 'branch', 'distributor', 'amount', 'currency', 'status'].map(
                        (key) => t(key)
                    )
                );
                sheet.getRow(2).font = { bold: true };
                rows.forEach((row) =>
                    sheet.addRow([
                        row.documentNumber,
                        date(row.issuedAt),
                        client(row),
                        row.branchName,
                        row.distributorName,
                        row.totalAmountMinor / 100,
                        row.currency,
                        t(row.status),
                    ])
                );
                totals.forEach((total) =>
                    sheet.addRow([t('issuedTotal'), '', '', '', '', total.amountMinor / 100, total.currency])
                );
                sheet.columns.forEach((column, index) => {
                    column.width = index === 2 ? 40 : 23;
                    if (index === 5) {
                        column.numFmt = '#,##0.00';
                        column.alignment = { horizontal: 'right' };
                    }
                });
            }
            saveAs(
                new Blob([await workbook.xlsx.writeBuffer()], {
                    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                }),
                `${t('exportFilePrefix')}-${afterDate}-${beforeDate}.xlsx`
            );
        } catch {
            dispatch({type: 'actionFailed'});
        } finally {
            dispatch({type: 'exportFinished'});
        }
    };

    return (
        <section className='w-100'>
            <h4>{t('title')}</h4>
            <div className='d-flex flex-wrap align-items-start gap-2 mt-3 mb-3'>
                <div style={{ width: 140 }}>
                    <DatePickerWrapper
                        id='deposit-after'
                        portalId='deposit-date-picker-portal'
                        selected={afterDate}
                        dateMask='dd/MM/yyyy'
                        onChange={(value) => dispatch({type: 'dateChanged', field: 'afterDate', value})}
                        customInput={
                            <TextField fullWidth size='small' label={t('from')} InputLabelProps={{ shrink: true }} />
                        }
                    />
                </div>
                <div style={{ width: 140 }}>
                    <DatePickerWrapper
                        id='deposit-before'
                        portalId='deposit-date-picker-portal'
                        selected={beforeDate}
                        dateMask='dd/MM/yyyy'
                        onChange={(value) => dispatch({type: 'dateChanged', field: 'beforeDate', value})}
                        customInput={
                            <TextField fullWidth size='small' label={t('to')} InputLabelProps={{ shrink: true }} />
                        }
                    />
                </div>
                <Autocomplete
                    size='small'
                    sx={{ minWidth: 220 }}
                    id='deposit-assignee'
                    options={users}
                    value={users.find((user) => user.id === distributorId) ?? null}
                    getOptionLabel={(option) => option.name}
                    isOptionEqualToValue={(a, b) => a.id === b.id}
                    onChange={(_, value) => dispatch({type: 'distributorChanged', value: value?.id})}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label={t('distributor')}
                            InputLabelProps={{ shrink: true }}
                            placeholder={t('all')}
                        />
                    )}
                />
                <Autocomplete
                    size='small'
                    sx={{ minWidth: 240 }}
                    options={clients.items}
                    value={selectedClient}
                    loading={clients.status === 'loading'}
                    disabled={clients.status === 'loading'}
                    getOptionLabel={(option) => option.name}
                    isOptionEqualToValue={(a, b) => a.id === b.id}
                    onChange={(_, value) => dispatch({type: 'clientChanged', value})}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label={t('client')}
                            InputLabelProps={{ shrink: true }}
                            placeholder={t('all')}
                        />
                    )}
                />
                <Autocomplete
                    size='small'
                    sx={{ minWidth: 240 }}
                    options={branches.items}
                    value={selectedBranch}
                    loading={branches.status === 'loading'}
                    disabled={!selectedClient || branches.status === 'loading'}
                    getOptionLabel={(option) => option.name}
                    isOptionEqualToValue={(a, b) => a.id === b.id}
                    onChange={(_, value) => dispatch({type: 'branchChanged', value})}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label={t('branch')}
                            InputLabelProps={{ shrink: true }}
                            placeholder={t('all')}
                        />
                    )}
                />
            </div>
            {!validDates && <Alert variant='warning'>{t('invalidDates')}</Alert>}
            {actionError && <Alert variant='danger'>{t('error')}</Alert>}
            {(clients.status === 'error' || branches.status === 'error') && (
                <Alert variant='danger'>
                    {t('error')} <Button onClick={() => dispatch({type: 'directoriesRetried'})}>{t('retry')}</Button>
                </Alert>
            )}
            <div className='d-flex justify-content-between align-items-center flex-wrap gap-2'>
                <div className='d-flex align-items-center flex-wrap gap-1'>
                    <TextField
                        select
                        size='small'
                        sx={{ minWidth: 180 }}
                        id='deposit-sort'
                        label={t('sort')}
                        SelectProps={{ native: true }}
                        InputLabelProps={{ shrink: true }}
                        value={direction}
                        onChange={(event) => dispatch({type: 'directionChanged', value: event.target.value as 'ASC' | 'DESC'})}
                    >
                        <option value='ASC'>{t('ASC')}</option>
                        <option value='DESC'>{t('DESC')}</option>
                    </TextField>
                    <PageSizeComponent pagination={pageSizeSelection} itemsToShow={[5, 10, 20, 50, 75, 100]} />
                </div>
                <Button onClick={exportReport} disabled={!validDates || exporting}>
                    {exporting ? t('exporting') : t('export')}
                </Button>
            </div>
            {validDates &&
                types.map((type) => (
                    <OrderTable
                        key={`${type}-${JSON.stringify(filters)}-${pageSizeSelection.pageSize}`}
                        pageSize={pageSizeSelection.pageSize}
                        filters={filters}
                        type={type}
                        onPrint={print}
                    />
                ))}
        </section>
    );
};
